import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from '@mui/material';
import { useAuthHeader } from '../../../hooks/useAuth';
import type { SimpleAccount } from '@/lib/types';
import { getWarehouseSellerMappings, type WarehouseSellerMappingRow } from '../../../lib/api';
import { downloadCSV } from '../../../lib/utils';

interface Props {
  account: SimpleAccount;
}

export function WarehouseSellerMappingList({ account }: Props) {
  const { getAuthHeader, isLoading: authHeaderLoading } = useAuthHeader();
  const [rows, setRows] = useState<WarehouseSellerMappingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      if (initialized && rows.length > 0) return;
      try {
        setLoading(true);
        setError(null);
        if (authHeaderLoading) return;
        const authHeaders = getAuthHeader();
        const data = await getWarehouseSellerMappings(account.code, authHeaders);
        setRows(data);
        setInitialized(true);
      } catch (e) {
        console.error(e);
        setError('店舗倉庫マッピング一覧の読み込みに失敗しました。');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [account.code, authHeaderLoading, getAuthHeader]);

  const handleDownloadCSV = () => {
    const csvData = rows.map((r) => ({
      店舗カスタマーID: r.seller_customer_id,
      店舗名: r.seller_name,
      倉庫カスタマーID: r.warehouse_customer_id,
      倉庫名: r.warehouse_name,
    }));
    const filename = `店舗倉庫マッピング一覧_${account.code}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvData, filename);
  };

  if (authHeaderLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <CircularProgress size={20} />
        <Typography sx={{ ml: 2 }}>認証状態を確認中...</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <CircularProgress size={20} />
        <Typography sx={{ ml: 2 }}>店舗倉庫マッピング一覧を読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">店舗倉庫マッピング一覧 ({rows.length}件)</Typography>
            {rows.length > 0 && (
              <Button variant="outlined" onClick={handleDownloadCSV} size="small">
                CSVでダウンロード
              </Button>
            )}
          </Box>

          {rows.length === 0 ? (
            <Alert severity="info">データが見つかりませんでした。</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>店舗カスタマーID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>店舗名</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>倉庫カスタマーID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>倉庫名</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, idx) => (
                    <TableRow key={`${r.seller_customer_id}-${r.warehouse_customer_id}-${idx}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.seller_customer_id}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.seller_name}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.warehouse_customer_id}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.warehouse_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
